import {
    type JournalData,
    type JournalPayload,
    MAX_TEMP,
    type MeasureRecord,
    MIN_TEMP,
    type ScaleData,
    type SleepScaleData,
    type TemperatureScaleData,
} from "@qendo/profile/journal";
import {
    LegacyJournalId,
    LegacySymptomId,
    ScaleType,
    type TrackableData,
    type TrackableMeasure,
    TrackableType,
} from "@qendo/trackables";
import { type DateArg, isSameDay, isSameMinute, min, roundToNearestMinutes, toDate } from "date-fns";
import { useEffect, useId, useMemo, useState } from "react";
import { Button } from "~/components/Button";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { JournalTimePicker } from "~/components/form/date-picker/JournalTimePicker";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { ScaleInput } from "~/components/form/journal/scale/ScaleInput";
import { TreatmentSelector } from "~/components/form/journal/TreatmentSelector";
import { YesNo } from "~/components/form/radio/YesNo";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Title } from "~/components/layout/text";
import { Only } from "~/components/permission/Only";
import { Space } from "~/constants/sizing";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useJournal } from "~/providers/JournalProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useReport } from "~/providers/ReportProvider";
import { useTrackable } from "~/providers/TrackableProvider";
import { filled } from "~/util";
import { elementsMatch, filterUnique } from "~/util/array";
import { mergeDateTime } from "~/util/dates";

import { makeMeasureRecord } from "~/util/qendo/measure";
import { isReportable } from "~/util/qendo/reports";
import { makeNewScale } from "~/util/qendo/scale";

type EditJournalProps<T extends TrackableType, S extends ScaleType> = {
    readonly type?: never;
    readonly journal: JournalData<T, S>;
};

type CreateJournalProps<T extends TrackableType, S extends ScaleType> = {
    readonly type: TrackableData<T, S>;
    readonly journal?: never;
};

export type JournalFormProps<T extends TrackableType = TrackableType, S extends ScaleType = ScaleType> = {
    readonly type?: TrackableData<T, S> | string;
    readonly journal?: JournalData<T, S>;
    onRequestClose: () => void;
    onDate?: string | DateArg<Date>;
} & (EditJournalProps<T, S> | CreateJournalProps<T, S>) &
    Partial<FullPageModalProps>;

export const JournalForm = <T extends TrackableType = TrackableType, S extends ScaleType = ScaleType>({
    type,
    journal,
    onRequestClose,
    onDate,
    ...modalProps
}: JournalFormProps<T, S>) => {
    const { findTrackable } = useTrackable();
    const { storeJournal } = useJournal();
    const { saving, setSaving } = useFeedback();
    const [dirty, setDirty] = useState(false);
    const { refreshReports } = useReport();
    const { can, cycle, refreshCycle } = useProfile();
    const id = useId();

    const trackable = useMemo(
        () => type ?? (findTrackable(journal?.trackable_id) as TrackableData<T, S>),
        [findTrackable, journal?.trackable_id, type],
    );
    const is_activity = trackable.type === TrackableType.activity;
    const is_symptom = trackable.type === TrackableType.symptom;
    const is_trackable = is_activity || is_symptom;
    const is_flare = trackable.legacy_id === LegacySymptomId.flare;
    const is_food = trackable.legacy_id === LegacyJournalId.food;
    const is_journal = trackable.legacy_id === LegacyJournalId.journal;
    const is_describable = is_trackable || is_food || is_journal;
    const is_period = trackable.legacy_id === LegacyJournalId.period;

    // biome-ignore lint/correctness/useExhaustiveDependencies(onDate): value to set
    const current = useMemo(() => {
        const now = new Date();
        return roundToNearestMinutes(onDate && !isSameDay(now, onDate) ? mergeDateTime(min([now, onDate]), now) : now, {
            nearestTo: 15,
            roundingMethod: "floor",
        });
    }, []);

    const [scale, setScale] = useState<ScaleData<S>>(journal?.scale ?? makeNewScale(trackable.scale));
    const [measures, setMeasures] = useState<MeasureRecord>(
        journal ? (journal.measures ?? {}) : makeMeasureRecord(trackable.measures),
    );
    const [flare, setFlare] = useState<boolean>(journal?.flare ?? is_flare);
    const [description, setDescription] = useState<string>(journal?.description ?? "");
    const [at, setAt] = useState<DateArg<Date>>(toDate(journal?.recorded_at ?? current));
    const [photographs, setPhotographs] = useState<string[]>(journal?.photograph_ids ?? []);
    const [flag, setFlag] = useState<boolean>(journal?.flag ?? false);
    const [treatments, setTreatments] = useState<string[]>(journal?.treatment_ids ?? []);
    const [treatment, setTreatment] = useState<string | null>(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies(current): initial time
    // biome-ignore lint/correctness/useExhaustiveDependencies(is_flare): read only
    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                flare !== (journal?.flare ?? is_flare) ||
                description !== (journal?.description ?? "") ||
                !isSameMinute(at, journal?.recorded_at ?? current) ||
                !elementsMatch(photographs, journal?.photograph_ids ?? []) ||
                flag !== (journal?.flag ?? false) ||
                !elementsMatch(treatments, journal?.treatment_ids ?? []),
        );
    }, [journal, flare, description, at, photographs, flag, treatments]);

    const description_title = (is_journal && "What's been going on?") || (is_food && "Meal details") || "Description";

    const updateMeasure = (type: TrackableMeasure, value: string | null) => {
        setMeasures({
            ...measures,
            [type]: value,
        });
        setDirty(true);
    };

    const updateScale = (value: ScaleData<S>) => {
        setScale(value);
        setDirty(true);
    };

    useEffect(() => {
        if (treatment) {
            setTreatment(null);
            setTreatments([...treatments, treatment].filter(filterUnique));
        }
    }, [treatment, treatments]);

    // biome-ignore lint/correctness/useExhaustiveDependencies(updateMeasure): callback
    const measurements = useMemo(
        () =>
            (trackable.measures ?? []).map((m, idx, a) => ({
                ...m,
                zIndex: a.length - idx,
                value: measures[m.type] ?? null,
                optional: !m.required,
                onItemSelected: (value: string | null) => updateMeasure(m.type, value),
            })),
        [trackable.measures, measures],
    );

    const canSave = useMemo(() => {
        if (saving) {
            return false;
        }

        if (is_journal) {
            return filled(description);
        }

        for (const m of measurements ?? []) {
            if (m.required && !m.value) {
                return false;
            }
        }

        switch (trackable.scale) {
            case ScaleType.none:
            case ScaleType.water:
                return true;
            case ScaleType.sleep: {
                const { start, end } = scale as SleepScaleData;

                return Boolean(start && end);
            }
            case ScaleType.temperature: {
                const temp = (scale as TemperatureScaleData).value ?? 0;

                return temp >= MIN_TEMP && temp <= MAX_TEMP;
            }
            default:
                return scale.value !== null;
        }
    }, [saving, trackable.scale, measurements, scale, is_journal, description]);

    const doSave = () => {
        if (!canSave || saving) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        const identifier = journal ? { id: journal.id } : { trackable_id: trackable.id };
        const should_refresh_cycle = is_period && cycle.tracked && can("view", "cycle");
        const should_refresh_reports = isReportable(trackable);

        const payload = {
            ...identifier,
            type: trackable.type,
            scale,
            flare,
            measures,
            flag,
            treatments,
            recorded_at: at,
            description: description.trim() || null,
            photograph_ids: photographs,
        } as JournalPayload<S, T>;

        storeJournal(payload)
            .then(() => {
                should_refresh_cycle && refreshCycle();
                should_refresh_reports && refreshReports();
            })
            .then(onRequestClose)
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            cancelable
            title={`Log ${is_trackable ? trackable.type.toLowerCase() : trackable.title.toLowerCase()}`}
            onRequestClose={onRequestClose}
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            gap={Space.lg}
        >
            <JournalTimePicker
                value={at}
                onDateSelected={setAt}
                disabled={is_saving}
                mode={is_period ? "date" : "datetime"}
            />
            <Only when={is_trackable}>
                <Title size="xl">{trackable.title}</Title>
                <Only not when={is_flare}>
                    <CheckboxInput
                        disabled={is_saving}
                        label="Triggered a flare"
                        value={flare}
                        onValueChange={setFlare}
                        icon="zap"
                    />
                </Only>
            </Only>
            <Only when={measurements.length}>
                <FieldGroup zIndex={50}>
                    {measurements.map((m) => (
                        <OptionSelector<TrackableMeasure>
                            label={m.title}
                            optional={m.optional}
                            key={m.type}
                            type={m.type}
                            placeholder={m.prompt}
                            value={m.value}
                            onItemSelected={m.onItemSelected}
                            clearable
                            name={`measures.${m.type}`}
                        />
                    ))}
                </FieldGroup>
            </Only>

            <Only when={trackable.scale !== ScaleType.none}>
                <ScaleInput<S>
                    scale={trackable.scale}
                    label={trackable.scale_name}
                    value={scale}
                    onUpdateScale={updateScale}
                    disabled={is_saving}
                    zIndex={30}
                />
            </Only>

            <Only when={is_symptom}>
                <FieldGroup>
                    <YesNo
                        title="Did this symptom stop you from doing usual activities (e.g. work, school)?"
                        optional
                        value={flag}
                        onValueChange={setFlag}
                        disabled={is_saving}
                    />
                    <TreatmentSelector
                        value={treatments}
                        onChangeSelection={setTreatments}
                        optional
                        name="treatments"
                        disabled={is_saving}
                    />
                </FieldGroup>
            </Only>

            <Only when={is_describable}>
                <FieldGroup zIndex={10}>
                    <TextArea
                        label={description_title}
                        value={description}
                        onChangeText={setDescription}
                        optional={!is_journal}
                        disabled={is_saving}
                        name="description"
                    />
                    <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
                </FieldGroup>
            </Only>
        </FullPageModal>
    );
};
